import React, { useState, useEffect } from "react";
import {
  Button,
  Card,
  CardBody,
  FormGroup,
  Form,
  Input,
  Container,
  Row,
  Col,
  FormFeedback,
  Table,
  Badge,
} from "reactstrap";
import UserHeader from "components/Headers/UserHeader.js";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const TwitterPosts = () => {
  const [formData, setFormData] = useState({
    postUrl: "",
    order: 0,
    isActive: true,
  });

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [errors, setErrors] = useState({});

  // Get token from localStorage
  const getToken = () => {
    return localStorage.getItem("token");
  };

  // Auth fetch function
  const authFetch = async (url, options = {}) => {
    const token = getToken();

    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      ...options.headers,
    };

    const response = await fetch(url, { ...options, headers });

    if (response.status === 401 || response.status === 403) {
      localStorage.removeItem("token");
      window.location.href = "/auth/login";
      throw new Error("Authentication failed");
    }

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.message || `HTTP error! status: ${response.status}`,
      );
    }

    return response.json();
  };

  // Fetch all Twitter posts
  const fetchPosts = async () => {
    setLoading(true);
    try {
      const data = await authFetch(
        `${process.env.REACT_APP_API_URL}/twitter-post`,
      );
      setPosts(data.posts || []);
    } catch (error) {
      console.error("Error fetching Twitter posts:", error);
      toast.error(error.message || "Failed to fetch Twitter posts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, []);

  // Handle form changes
  const handleChange = (e) => {
    const { name, value, type } = e.target;
    let processedValue = value;

    // Handle boolean select
    if (type === "select" && name === "isActive") {
      processedValue = value === "true";
    }

    setFormData({
      ...formData,
      [name]: processedValue,
    });
    // Clear error for this field
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
  };

  // Validate form
  const validateForm = () => {
    let formErrors = {};
    let valid = true;

    if (!formData.postUrl || formData.postUrl.trim() === "") {
      formErrors.postUrl = "Twitter post URL is required!";
      valid = false;
    } else {
      // Validate Twitter/X URL format
      const twitterUrlPattern =
        /^https?:\/\/(twitter\.com|x\.com)\/.+\/status\/\d+/i;
      if (!twitterUrlPattern.test(formData.postUrl.trim())) {
        formErrors.postUrl =
          "Invalid Twitter URL format. Expected: https://twitter.com/username/status/123456";
        valid = false;
      }
    }

    if (
      formData.order === "" ||
      isNaN(formData.order) ||
      parseInt(formData.order) < 0
    ) {
      formErrors.order = "Order must be a non-negative number!";
      valid = false;
    }

    setErrors(formErrors);
    return valid;
  };

  // Handle form submit (create or update)
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      const postData = {
        postUrl: formData.postUrl.trim(),
        order: parseInt(formData.order),
        isActive: formData.isActive,
      };

      if (editingPost) {
        // Update existing post
        await authFetch(
          `${process.env.REACT_APP_API_URL}/twitter-post/${editingPost.id}`,
          {
            method: "PUT",
            body: JSON.stringify(postData),
          },
        );
        toast.success("Twitter post updated successfully!");
      } else {
        // Create new post
        await authFetch(`${process.env.REACT_APP_API_URL}/twitter-post`, {
          method: "POST",
          body: JSON.stringify(postData),
        });
        toast.success("Twitter post created successfully!");
      }

      // Reset form and refresh list
      setFormData({ postUrl: "", order: 0, isActive: true });
      setEditingPost(null);
      setErrors({});
      fetchPosts();
    } catch (error) {
      console.error("Error saving Twitter post:", error);
      toast.error(error.message || "Failed to save Twitter post");
    }
  };

  // Handle edit
  const handleEdit = (post) => {
    setEditingPost(post);
    setFormData({
      postUrl: post.postUrl,
      order: post.order,
      isActive: post.isActive,
    });
    // Scroll to form
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Handle delete
  const handleDelete = async (postId) => {
    if (!window.confirm("Are you sure you want to delete this Twitter post?")) {
      return;
    }

    try {
      await authFetch(
        `${process.env.REACT_APP_API_URL}/twitter-post/${postId}`,
        {
          method: "DELETE",
        },
      );
      toast.success("Twitter post deleted successfully!");
      fetchPosts();
    } catch (error) {
      console.error("Error deleting Twitter post:", error);
      toast.error(error.message || "Failed to delete Twitter post");
    }
  };

  // Handle cancel edit
  const handleCancel = () => {
    setEditingPost(null);
    setFormData({ postUrl: "", order: 0, isActive: true });
    setErrors({});
  };

  return (
    <>
      <UserHeader />
      <Container
        className="mt--7"
        style={{ background: "#251d0c", paddingBottom: "100px" }}
        fluid
      >
        <Row>
          <Col className="order-xl-1" xl="12">
            <Card className="bg-dark">
              <CardBody style={{ background: "black" }}>
                <Form onSubmit={handleSubmit}>
                  <h6
                    className="heading-small text-light mb-4"
                    style={{ fontSize: "1.5rem" }}
                  >
                    {editingPost ? "Edit Twitter Post" : "Create Twitter Post"}
                  </h6>
                  <div className="pl-lg-4">
                    <Row>
                      <Col lg="12">
                        <FormGroup>
                          <label
                            style={{ fontSize: "1.4rem" }}
                            className="form-control-label text-light"
                            htmlFor="input-postUrl"
                          >
                            Twitter Post URL
                          </label>
                          <Input
                            name="postUrl"
                            value={formData.postUrl}
                            onChange={handleChange}
                            placeholder="https://twitter.com/username/status/123456"
                            type="text"
                            style={{
                              backgroundColor: "#333",
                              color: "#FFB000",
                              fontSize: "1.2rem",
                            }}
                            invalid={!!errors.postUrl}
                          />
                          {errors.postUrl && (
                            <FormFeedback>{errors.postUrl}</FormFeedback>
                          )}
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col lg="6">
                        <FormGroup>
                          <label
                            style={{ fontSize: "1.4rem" }}
                            className="form-control-label text-light"
                            htmlFor="input-order"
                          >
                            Display Order
                          </label>
                          <Input
                            name="order"
                            value={formData.order}
                            onChange={handleChange}
                            placeholder="0"
                            type="number"
                            min="0"
                            style={{
                              backgroundColor: "#333",
                              color: "#FFB000",
                              fontSize: "1.2rem",
                            }}
                            invalid={!!errors.order}
                          />
                          {errors.order && (
                            <FormFeedback>{errors.order}</FormFeedback>
                          )}
                          <small className="text-muted">
                            Lower numbers appear first
                          </small>
                        </FormGroup>
                      </Col>

                      <Col lg="6">
                        <FormGroup>
                          <label
                            style={{ fontSize: "1.4rem" }}
                            className="form-control-label text-light"
                            htmlFor="input-isActive"
                          >
                            Status
                          </label>
                          <Input
                            name="isActive"
                            value={formData.isActive ? "true" : "false"}
                            onChange={handleChange}
                            type="select"
                            style={{
                              backgroundColor: "#333",
                              color: "#FFB000",
                              fontSize: "1.2rem",
                            }}
                          >
                            <option value="true">Active</option>
                            <option value="false">Inactive</option>
                          </Input>
                        </FormGroup>
                      </Col>
                    </Row>

                    <div className="d-flex gap-2">
                      <Button
                        color="primary"
                        size="lg"
                        type="submit"
                        style={{
                          backgroundColor: "#FFB000",
                          color: "#222",
                          fontSize: "1.4rem",
                        }}
                      >
                        {editingPost ? "Update Post" : "Create Post"}
                      </Button>
                      {editingPost && (
                        <Button
                          color="secondary"
                          size="lg"
                          type="button"
                          onClick={handleCancel}
                          style={{
                            backgroundColor: "#666",
                            color: "#fff",
                            fontSize: "1.4rem",
                          }}
                        >
                          Cancel
                        </Button>
                      )}
                    </div>
                  </div>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Posts List */}
        <Row className="mt-4">
          <Col xl="12">
            <Card className="bg-dark">
              <CardBody style={{ background: "black" }}>
                <h6
                  className="heading-small text-light mb-4"
                  style={{ fontSize: "1.5rem" }}
                >
                  Twitter Posts List
                </h6>
                {loading ? (
                  <div className="text-center text-light">Loading...</div>
                ) : posts.length === 0 ? (
                  <div className="text-center text-light">
                    No Twitter posts found. Create your first post above.
                  </div>
                ) : (
                  <Table dark responsive>
                    <thead>
                      <tr>
                        <th>Order</th>
                        <th>Post URL</th>
                        <th>Status</th>
                        <th>Created</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {posts.map((post) => (
                        <tr key={post.id}>
                          <td>{post.order}</td>
                          <td>
                            <a
                              href={post.postUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: "#FFB000" }}
                            >
                              {post.postUrl.length > 50
                                ? post.postUrl.substring(0, 50) + "..."
                                : post.postUrl}
                            </a>
                          </td>
                          <td>
                            <Badge
                              color={post.isActive ? "success" : "secondary"}
                            >
                              {post.isActive ? "Active" : "Inactive"}
                            </Badge>
                          </td>
                          <td>
                            {new Date(post.createdAt).toLocaleDateString()}
                          </td>
                          <td>
                            <Button
                              color="info"
                              size="sm"
                              onClick={() => handleEdit(post)}
                              className="mr-2"
                            >
                              Edit
                            </Button>
                            <Button
                              color="danger"
                              size="sm"
                              onClick={() => handleDelete(post.id)}
                            >
                              Delete
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
      <ToastContainer />
    </>
  );
};

export default TwitterPosts;
